import { groupBy, countBy } from 'lodash';
import { TENANT_TYPES } from '../constants/tenant-types';
import { isBeta } from '../environment';
import { MAIN_BRAND_NAME } from '../constants/user-team-ids';
import { FEATURE_FLAGS } from '../constants/feature-flags';
import * as UserUtils from './user-utils';
import * as EngagementTypeUtils from './engagement-type-utils';

export const getAllowedToCreateTenantTypes = (userInfo, parentCustomer, flags) => {
  /** TODO - need to check permission - if logged in tenant is allowed to create tenant (write permissions) - similar to CustomersToolbar */
  const isMultiTenantFeatureEnabled = Boolean(flags?.[FEATURE_FLAGS.MULTI_TENANT_TENANT_TYPE]);
  const isTemEssentialsTenantEnabled = isBeta || Boolean(flags?.[FEATURE_FLAGS.TEM_ESSENTIALS_TENANT_VISIBILITY]);

  if (!isMultiTenantFeatureEnabled && userInfo?.customer?.tenant_type === TENANT_TYPES.MULTI_TENANT_MANAGEMENT) {
    return [];
  }

  const { tenant_type: loggedInTenantType } = userInfo.customer;
  const parentTenantType = parentCustomer?.tenant_type;

  if (!parentTenantType || !UserUtils.isAdminUser(userInfo)) {
    return undefined;
  }

  // Logged In to cyberint.cyberint.io or yg-mms-mgmt.staging.cyberint.io
  if (loggedInTenantType === TENANT_TYPES.CYBERINT_MANAGEMENT && UserUtils.isUserInTeam(userInfo, MAIN_BRAND_NAME)) {
    switch (parentTenantType) {
      case TENANT_TYPES.CYBERINT_MANAGEMENT: {
        const types = [
          TENANT_TYPES.CYBERINT_MANAGEMENT,
          TENANT_TYPES.CYBERINT_CUSTOMER,
          TENANT_TYPES.CYBERINT_PROVISIONING,
          TENANT_TYPES.MSSP_MANAGEMENT,
          TENANT_TYPES.MULTI_TENANT_MANAGEMENT,
          TENANT_TYPES.PARTNERS_VAR_MANAGEMENT,
          ...(isTemEssentialsTenantEnabled
            ? [TENANT_TYPES.TEM_ESSENTIALS_PROVISIONING, TENANT_TYPES.TEM_ESSENTIALS_PRODUCTION]
            : [])
        ];
        return isMultiTenantFeatureEnabled
          ? types
          : types.filter(type => type !== TENANT_TYPES.MULTI_TENANT_MANAGEMENT);
      }
      case TENANT_TYPES.CYBERINT_CUSTOMER:
        return [
          TENANT_TYPES.CYBERINT_CUSTOMER,
          TENANT_TYPES.CYBERINT_PROVISIONING,
          ...(isTemEssentialsTenantEnabled
            ? [TENANT_TYPES.TEM_ESSENTIALS_PROVISIONING, TENANT_TYPES.TEM_ESSENTIALS_PRODUCTION]
            : [])
        ];
      case TENANT_TYPES.CYBERINT_PROVISIONING:
        return [TENANT_TYPES.CYBERINT_PROVISIONING];
      case TENANT_TYPES.MSSP_MANAGEMENT:
        return [
          TENANT_TYPES.MSSP_MANAGEMENT,
          TENANT_TYPES.MSSP_PROVISIONING,
          TENANT_TYPES.MSSP_PARENT_ALLOCATION,
          TENANT_TYPES.MSSP_STANDALONE_ALLOCATION,
          TENANT_TYPES.MSSP_REPORT
        ];
      case TENANT_TYPES.MSSP_PROVISIONING:
        return [TENANT_TYPES.MSSP_PROVISIONING];
      case TENANT_TYPES.PARTNERS_VAR_MANAGEMENT:
        return [TENANT_TYPES.PARTNERS_VAR_PROVISIONING];
      case TENANT_TYPES.MULTI_TENANT_MANAGEMENT:
        return isMultiTenantFeatureEnabled ? [TENANT_TYPES.CYBERINT_CUSTOMER] : [];
      default:
        return [];
    }
  }
  // Logged In to Cyberint Management Account
  if (loggedInTenantType === TENANT_TYPES.CYBERINT_MANAGEMENT) {
    switch (parentTenantType) {
      case TENANT_TYPES.CYBERINT_MANAGEMENT:
        return [
          TENANT_TYPES.CYBERINT_PROVISIONING,
          TENANT_TYPES.MSSP_MANAGEMENT,
          ...(isTemEssentialsTenantEnabled ? [TENANT_TYPES.TEM_ESSENTIALS_PROVISIONING] : [])
        ];
      case TENANT_TYPES.CYBERINT_PROVISIONING:
        return [TENANT_TYPES.CYBERINT_PROVISIONING];
      default:
        return [];
    }
  }
  // Logged In to MSSP Management Account
  if (loggedInTenantType === TENANT_TYPES.MSSP_MANAGEMENT) {
    if (parentTenantType === TENANT_TYPES.MSSP_MANAGEMENT) {
      return [TENANT_TYPES.MSSP_PROVISIONING, TENANT_TYPES.MSSP_PARENT_ALLOCATION, TENANT_TYPES.MSSP_REPORT];
    }
    return [];
  }

  // Logged In to MT Management Account
  if (loggedInTenantType === TENANT_TYPES.MULTI_TENANT_MANAGEMENT) {
    if (parentTenantType === TENANT_TYPES.MULTI_TENANT_MANAGEMENT) {
      return [TENANT_TYPES.CYBERINT_CUSTOMER];
    }
    return [];
  }

  if (loggedInTenantType === TENANT_TYPES.PARTNERS_VAR_MANAGEMENT) {
    return parentTenantType === TENANT_TYPES.PARTNERS_VAR_MANAGEMENT ? [TENANT_TYPES.PARTNERS_VAR_PROVISIONING] : [];
  }

  return [];
};

export const getAllowedToConvertTenantTypes = customerToConvert => {
  const customerToConvertType = customerToConvert?.tenant_type;

  switch (customerToConvertType) {
    case TENANT_TYPES.CYBERINT_PROVISIONING:
      return EngagementTypeUtils.isTemEssentials(customerToConvert)
        ? [TENANT_TYPES.TEM_ESSENTIALS_PRODUCTION]
        : [TENANT_TYPES.CYBERINT_CUSTOMER];
    case TENANT_TYPES.MSSP_PROVISIONING:
      return [TENANT_TYPES.MSSP_PARENT_ALLOCATION, TENANT_TYPES.MSSP_STANDALONE_ALLOCATION];
    case TENANT_TYPES.PARTNERS_VAR_PROVISIONING:
      return [TENANT_TYPES.CYBERINT_CUSTOMER];
    default:
      return [];
  }
};

/** CYBERINT_CUSTOMER can also create sub children */
const PARENT_TENANTS = [
  TENANT_TYPES.MSSP_MANAGEMENT,
  TENANT_TYPES.CYBERINT_MANAGEMENT,
  TENANT_TYPES.CYBERINT_CUSTOMER,
  TENANT_TYPES.MULTI_TENANT_MANAGEMENT,
  TENANT_TYPES.PARTNERS_VAR_MANAGEMENT
];

const MGMT_TENANTS = [
  TENANT_TYPES.MSSP_MANAGEMENT,
  TENANT_TYPES.CYBERINT_MANAGEMENT,
  TENANT_TYPES.MULTI_TENANT_MANAGEMENT,
  TENANT_TYPES.PARTNERS_VAR_MANAGEMENT
];

const MSSP_TENANTS = [
  TENANT_TYPES.MSSP_STANDALONE_ALLOCATION,
  TENANT_TYPES.MSSP_PARENT_ALLOCATION,
  TENANT_TYPES.MSSP_MANAGEMENT,
  TENANT_TYPES.MSSP_PROVISIONING,
  TENANT_TYPES.MSSP_REPORT
];

const DIRECT_TENANTS = [
  TENANT_TYPES.MSSP_STANDALONE_ALLOCATION,
  TENANT_TYPES.MSSP_PARENT_ALLOCATION,
  TENANT_TYPES.CYBERINT_PROVISIONING,
  TENANT_TYPES.MSSP_PROVISIONING,
  TENANT_TYPES.CYBERINT_CUSTOMER,
  TENANT_TYPES.PARTNERS_VAR_PROVISIONING
];

const getTenantType = tenantOrType => (typeof tenantOrType === 'string' ? tenantOrType : tenantOrType?.tenant_type);

export const isDirectTenant = tenantOrType => DIRECT_TENANTS.includes(getTenantType(tenantOrType));

export const isMsspTenant = tenantOrType => MSSP_TENANTS.includes(getTenantType(tenantOrType));

export const isParentTenant = tenantOrType => PARENT_TENANTS.includes(getTenantType(tenantOrType));

export const isMgmtTenant = tenantOrType => MGMT_TENANTS.includes(getTenantType(tenantOrType));

export const isMsspParentAllocationTenant = tenantOrType =>
  getTenantType(tenantOrType) === TENANT_TYPES.MSSP_PARENT_ALLOCATION;

export const isMultiTenantMgmt = tenantOrType => getTenantType(tenantOrType) === TENANT_TYPES.MULTI_TENANT_MANAGEMENT;

export const isMsspMgmtTenant = tenantOrType => getTenantType(tenantOrType) === TENANT_TYPES.MSSP_MANAGEMENT;

export const isCyberintCustomerTenant = tenantOrType => getTenantType(tenantOrType) === TENANT_TYPES.CYBERINT_CUSTOMER;

export const isCyberintMgmtTenant = tenantOrType => getTenantType(tenantOrType) === TENANT_TYPES.CYBERINT_MANAGEMENT;

// An MT management parent's usage bar shows "the pool": capacity granted to children ("allocated")
// plus its own live count. Every other tenant type has no "allocated" term, so only its own count
// is shown. `utilized` is `{ total }` for asm/ti/coins but a flat number for users/th-users.
export const getPoolUsed = (tenantOrType, usage) => {
  const allocatedToChildren = isMultiTenantMgmt(tenantOrType) ? usage?.allocated || 0 : 0;
  const ownUtilized = typeof usage?.utilized === 'number' ? usage.utilized : usage?.utilized?.total ?? 0;
  return allocatedToChildren + ownUtilized;
};

const DIRECT_MSSP_TENANT = [
  TENANT_TYPES.MSSP_REPORT,
  TENANT_TYPES.MSSP_PROVISIONING,
  TENANT_TYPES.MSSP_PARENT_ALLOCATION,
  TENANT_TYPES.MSSP_STANDALONE_ALLOCATION
];
export const isDirectMsspTenant = tenantOrType => DIRECT_MSSP_TENANT.includes(getTenantType(tenantOrType));

const TENANTS_MANAGED_BY_CYBERINT = [
  TENANT_TYPES.CYBERINT_MANAGEMENT,
  TENANT_TYPES.CYBERINT_PROVISIONING,
  TENANT_TYPES.CYBERINT_CUSTOMER,
  TENANT_TYPES.MSSP_MANAGEMENT,
  TENANT_TYPES.PARTNERS_VAR_MANAGEMENT
];

export const isTenantManagedByCyberint = tenantOrType =>
  TENANTS_MANAGED_BY_CYBERINT.includes(getTenantType(tenantOrType));

const PROVISIONING_TENANTS = [
  TENANT_TYPES.CYBERINT_PROVISIONING,
  TENANT_TYPES.MSSP_PROVISIONING,
  TENANT_TYPES.PARTNERS_VAR_PROVISIONING
];

export const isProvisioningTenant = tenantOrType => PROVISIONING_TENANTS.includes(getTenantType(tenantOrType));

// Tenant types whose (legacy) environment_type was "production". Replaces
// environment_type === 'production' checks now that the field is removed.
const PRODUCTION_TENANTS = [
  TENANT_TYPES.CYBERINT_CUSTOMER,
  TENANT_TYPES.MSSP_MANAGEMENT,
  TENANT_TYPES.MSSP_PARENT_ALLOCATION,
  TENANT_TYPES.MSSP_STANDALONE_ALLOCATION,
  TENANT_TYPES.MSSP_REPORT
];

export const isProductionTenant = tenantOrType => PRODUCTION_TENANTS.includes(getTenantType(tenantOrType));

const TH_ENABLED_TENANTS = [
  TENANT_TYPES.CYBERINT_MANAGEMENT,
  TENANT_TYPES.CYBERINT_PROVISIONING,
  TENANT_TYPES.CYBERINT_CUSTOMER,
  TENANT_TYPES.MSSP_MANAGEMENT,
  TENANT_TYPES.MSSP_PARENT_ALLOCATION,
  TENANT_TYPES.MSSP_PROVISIONING,
  TENANT_TYPES.MULTI_TENANT_MANAGEMENT
];

export const isThTenant = tenantOrType => TH_ENABLED_TENANTS.includes(getTenantType(tenantOrType));

const TEM_ESSENTIALS_TENANTS = [TENANT_TYPES.TEM_ESSENTIALS_PROVISIONING, TENANT_TYPES.TEM_ESSENTIALS_PRODUCTION];
export const isTemEssentialsTenant = tenantOrType => TEM_ESSENTIALS_TENANTS.includes(getTenantType(tenantOrType));

export const isPartnersVarManagementTenant = tenantOrType =>
  getTenantType(tenantOrType) === TENANT_TYPES.PARTNERS_VAR_MANAGEMENT;

export const isPartnersVarProvisioningTenant = tenantOrType =>
  getTenantType(tenantOrType) === TENANT_TYPES.PARTNERS_VAR_PROVISIONING;

/**
 * The selected tenant's customer id, but only while the user is managing someone else's tenant.
 * Services that resolve the tenant from a request header (TIP's X-Cyberint-Customer-Id) expect it
 */
export const getManagedCustomerId = (selectedCustomerId, user) =>
  selectedCustomerId && selectedCustomerId !== user?.customer?._id ? selectedCustomerId : undefined;

const ACTIVE = 'active';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = id => typeof id === 'string' && UUID_RE.test(id);

// Suffix used to disambiguate tenants that share a name. For opaque UUID ids
// the last 5 chars are enough to tell them apart at a glance; legacy
// (non-UUID) ids are short and meaningful, so show them in full.
const tenantIdSuffix = id => (isUuid(id) ? id.slice(-5) : id);

const tenantId = c => c?.id || c?._id;

// Single source of truth for tenant-name disambiguation. Given a list of
// tenants, returns { [id]: displayName }:
//   - unique name            -> name as-is
//   - duplicate name         -> "Name (last-5-of-uuid | full-legacy-id)"
//   - still duplicate after  -> "Name (full id)"
// Reuse this anywhere tenant names are rendered (selector, folder trees,
// filters, TenantDisplay) so duplicates are handled identically.
export const buildTenantDisplayNameMap = (customers = []) => {
  const byName = groupBy(customers, 'name');
  const map = {};
  Object.values(byName).forEach(group => {
    if (group.length <= 1) {
      group.forEach(c => {
        map[tenantId(c)] = c.name ?? '';
      });
      return;
    }
    const labeled = group.map(c => ({ c, name: `${c.name} (${tenantIdSuffix(tenantId(c))})` }));
    const counts = countBy(labeled, 'name');
    labeled.forEach(({ c, name }) => {
      map[tenantId(c)] = counts[name] > 1 ? `${c.name} (${tenantId(c)})` : name;
    });
  });
  return map;
};

export const groupCustomers = customers => {
  const nameMap = buildTenantDisplayNameMap(customers);
  const customerGroupsByName = groupBy(customers, 'name');
  return Object.values(customerGroupsByName).flatMap(group => {
    if (group.length <= 1) return group;
    return [...group]
      .sort((a, b) => (b.status === ACTIVE) - (a.status === ACTIVE) || a.created_date - b.created_date)
      .map(customer => ({ ...customer, name: nameMap[tenantId(customer)], grouped: true }));
  });
};

export const MIN_CREDIT_ALLOCATION = 14;
